import * as React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import "./EngagementIncentives.css";
import ClickData from "./ClickData/ClickData";
import PerformanceDayBrands from "./PerformanceDayBrands/PerformanceDayBrands";
import BudgetControl from "./BudgetControl/BudgetControl";
import { useSelector } from "react-redux";
import { selectUserData } from "../../../features/userSlice";
import Page404 from "../../Page404/Page403";
import { useSearchParams } from "react-router-dom";
import { CheckPermissions } from "../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  selectedSiteUids: string[];
  userToken: any;
}

interface EngagementIncentivesTabsProps {
  selectedSiteUids: string[];
  userToken: any;
  siteSelectionTrigger: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, selectedSiteUids, userToken, ...other } =
    props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const EngagementIncentivesTabs = ({
  selectedSiteUids,
  userToken,
  siteSelectionTrigger,
}: EngagementIncentivesTabsProps) => {
  const [value, setValue] = React.useState(0);
  const userData = useSelector(selectUserData);

  const hasViewMonthlyBudgetPermission = CheckPermissions(
    userData?.data?.products?.paid_traffic,
    "advertising_accounts",
    "view_monthly_budgets"
  );
  const hasViewEngagementIncentivesPermission = CheckPermissions(
    userData?.data?.products?.paid_traffic,
    "advertising_accounts",
    "view_incentivezed_traffic_performance"
  );
  const [searchParams, setSearchParams] = useSearchParams();

  React.useEffect(() => {
    const tabFromParams = searchParams.get("tab");
    if (tabFromParams) {
      setValue(Number(tabFromParams)); // Load tab index from URL
    }
  }, [searchParams]);

  const handleChange = (_: unknown, newValue: number) => {
    setValue(newValue);
    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);

      // ✅ Remove the unwanted params
      updatedParams.delete("periodTab1");
      updatedParams.delete("startDateTab1");
      updatedParams.delete("endDateTab1");
      updatedParams.delete("periodTab2");
      updatedParams.delete("startDateTab2");
      updatedParams.delete("endDateTab2");
      updatedParams.delete("brandNameTab2");
      updatedParams.delete("brandNameTab3");
      updatedParams.delete("startDateTab3");
      updatedParams.delete("endDateTab3");
      updatedParams.delete("pagePerformanceByDayBrand");
      updatedParams.delete("pageBudgetControl");
      updatedParams.delete("pageClickData");

      updatedParams.set("tab", newValue.toString()); // Update 'tab' param
      return updatedParams;
    });
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={value}
            onChange={handleChange}
            indicatorColor="secondary"
            aria-label="basic tabs example"
            sx={{
              "& .MuiTab-root": {
                color: "#00000099", // Default color for unselected tabs
                "&.Mui-selected": {
                  color: "#9C27B0", // Change color when the tab is selected
                },
              },
              "& .MuiTabs-flexContainer": {
                justifyContent: "flex-start",
              },
              "& .MuiTab-root:first-of-type": {
                paddingLeft: 0,
              },
            }}
          >
            <Tab label="Budget control" {...a11yProps(0)} />
            <Tab label="Performance By Day And Brands" {...a11yProps(1)} />
            <Tab label="Click data" {...a11yProps(2)} />
          </Tabs>
        </Box>
        {/* Performance By Day And Brands tab */}
        {value === 0 &&
          (hasViewMonthlyBudgetPermission ? (
            <CustomTabPanel
              value={value}
              index={0}
              selectedSiteUids={selectedSiteUids}
              userToken={userToken}
            >
              <BudgetControl
                selectedSiteUids={selectedSiteUids}
                userToken={userToken}
                siteSelectionTrigger={siteSelectionTrigger}
              />
            </CustomTabPanel>
          ) : (
            <Page404 />
          ))}
        {value === 1 &&
          (hasViewEngagementIncentivesPermission ? (
            <CustomTabPanel
              value={value}
              index={1}
              selectedSiteUids={selectedSiteUids}
              userToken={userToken}
            >
              <PerformanceDayBrands
                selectedSiteUids={selectedSiteUids}
                userToken={userToken}
                siteSelectionTrigger={siteSelectionTrigger}
              />
            </CustomTabPanel>
          ) : (
            <Page404 />
          ))}

        {/* Click data tab */}
        {value === 2 &&
          (hasViewEngagementIncentivesPermission ? (
            <CustomTabPanel
              value={value}
              index={2}
              selectedSiteUids={selectedSiteUids}
              userToken={userToken}
            >
              <ClickData
                selectedSiteUids={selectedSiteUids}
                userToken={userToken}
                siteSelectionTrigger={siteSelectionTrigger}
              />
            </CustomTabPanel>
          ) : (
            <Page404 />
          ))}
      </Box>
    </>
  );
};

export default EngagementIncentivesTabs;
